import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function TesterModal({ show, onHide }) {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState('');
  const [stepMessage, setStepMessage] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const steps = [
    'Choose Doctor',
    'Pick Date & Time',
    'Upload Info',
    'Login / Register',
    'Confirmation',
  ];

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await axios.get('https://ricgop.online/api/get-doctors.php');
        setDoctors(res.data);
      } catch (err) {
        console.error('Failed to fetch doctors', err);
      }
    };
    fetchDoctors();
  }, []);

  const formatDate = (dateObj) => {
    if (!dateObj) return '';
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${year}-${month}-${day}`;
  };

 useEffect(() => {
  const fetchSlots = async () => {
    if (formData.doctor_id && formData.date instanceof Date) {
      const formattedDate = formatDate(formData.date);
      try {
        const res = await axios.get(
          `https://ricgop.online/api/get_slots.php?doctor_id=${formData.doctor_id}&date=${formattedDate}`
        );

        const today = new Date();
        const selectedDate = new Date(formData.date);
        selectedDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        let availableSlots = res.data?.slots || [];

        // Filter based on today's time if date is today
        if (selectedDate.getTime() === today.getTime()) {
          const currentTime = new Date().toTimeString().slice(0, 5); // "HH:MM"
          availableSlots = availableSlots.filter(slot => slot.time > currentTime);
        }

        // If selected date is in the past, show no slots
        if (selectedDate < today) {
          availableSlots = [];
        }

        setSlots({ slots: availableSlots });

      } catch (err) {
        console.error('Failed to fetch slots', err);
        setSlots([]);
      }
    } else {
      setSlots([]);
    }
  };

  fetchSlots();
}, [formData.doctor_id, formData.date]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => setFile(e.target.files[0]);

  const isStepValid = (index) => {
    switch (index) {
      case 0:
        return formData.doctor_id;
      case 1:
        return formData.date && formData.time;
      case 3:
        return formData.email && formData.password;
      default:
        return true;
    }
  };

  const validatePreviousSteps = (targetStep) => {
    for (let i = 0; i < targetStep; i++) {
      if (!isStepValid(i)) return false;
    }
    return true;
  };

  const showStepMessage = (msg) => {
    setStepMessage(msg);
    setTimeout(() => setStepMessage(''), 10000);
  };

  const goToStep = (index) => {
    if (index <= step || validatePreviousSteps(index)) {
      setStep(index);
    } else {
      showStepMessage('Please complete the previous steps first.');
    }
  };

  const next = () => {
    if (isStepValid(step)) {
      setStep((s) => Math.min(s + 1, steps.length - 1));
    } else {
      showStepMessage('Please fill out the required fields before proceeding.');
    }
  };

  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    const data = new FormData();
    for (const key in formData) {
      if (key === 'date' && formData.date instanceof Date) {
        data.append(key, formatDate(formData.date));
      } else {
        data.append(key, formData[key]);
      }
    }
    if (file) data.append('file', file);

    setLoading(true);
    try {
      const response = await axios.post('https://ricgop.online/api/book.php', data);
      setMessage(response.data.message || 'Appointment booked successfully!');
      setTimeout(() => {
        onHide(); // auto-close modal
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error.response?.data || error.message);
      setMessage('Error submitting form.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <Form.Group className="mb-4">
            <Form.Label>Select Doctor</Form.Label>
            <Form.Select name="doctor_id" onChange={handleChange} value={formData.doctor_id || ''}>
              <option value="">Choose a doctor</option>
              {doctors.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        );
      case 1:
        return (
          <>
            <Form.Group className="mb-4">
              <Form.Label>Date</Form.Label>
              <DatePicker
                selected={formData.date || null}
                onChange={(date) => setFormData({ ...formData, date })}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                placeholderText="YYYY-MM-DD"
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Available Time Slots</Form.Label>
              <Form.Select name="time" onChange={handleChange} value={formData.time || ''}>
                <option value="">Select a time slot</option>
                {slots.slots?.length > 0 ? (
                  slots.slots.map((slot, idx) => (
                    <option key={idx} value={slot.time}>{slot.time}</option>
                  ))
                ) : (
                  <option disabled>{formData.date ? 'No slots available' : 'Please select a date'}</option>
                )}
              </Form.Select>
            </Form.Group>
          </>
        );
      case 2:
        return (
          <>
            <Form.Group className="mb-4">
              <Form.Label>Upload Document</Form.Label>
              <Form.Control type="file" onChange={handleFileChange} accept=".pdf,.doc,.docx,.jpg,.png" />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Explanation</Form.Label>
              <Form.Control as="textarea" rows={3} name="explanation" placeholder="Explain your condition" onChange={handleChange} />
            </Form.Group>
          </>
        );
      case 3:
        return (
          <>
            <Form.Group className="mb-4">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" name="email" placeholder="Enter your email" onChange={handleChange} />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" name="password" placeholder="Enter your password" onChange={handleChange} />
            </Form.Group>
            <Alert variant="info" className="rounded-3 shadow-sm">
              Don't have an account? Enter your email & password to register automatically.
            </Alert>
          </>
        );
      case 4:
        return (
          <>
            <h5 className="mb-3 text-center">Confirm Your Information</h5>
            <ul className="list-group">
              <li className="list-group-item"><strong>Doctor:</strong> {doctors.find(d => d.id === formData.doctor_id)?.name || '-'}</li>
              <li className="list-group-item"><strong>Date:</strong> {formatDate(formData.date) || '-'}</li>
              <li className="list-group-item"><strong>Time:</strong> {formData.time || '-'}</li>
              <li className="list-group-item"><strong>Email:</strong> {formData.email || '-'}</li>
              <li className="list-group-item"><strong>Explanation:</strong> {formData.explanation || '-'}</li>
              <li className="list-group-item"><strong>File:</strong> {file?.name || 'No file uploaded'}</li>
            </ul>
          </>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!show) {
      setStep(0);
      setFormData({});
      setFile(null);
      setMessage('');
      setStepMessage('');
      setSlots([]);
      setLoading(false);
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      contentClassName="modern-modal"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="w-100 text-center">
          Book Appointment — Step {step + 1}: {steps[step]}
        </Modal.Title>
      </Modal.Header>

      {/* Progress bar */}
      <ProgressBar
        now={((step + 1) / steps.length) * 100}
        label={`${Math.round(((step + 1) / steps.length) * 100)}%`}
        className="rounded-0"
      />

      <Modal.Body>
        {stepMessage && <Alert variant="warning">{stepMessage}</Alert>}
        {message && <Alert variant="success">{message}</Alert>}
        <Form>{renderStep()}</Form>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between border-0">
        {step > 0 && <Button variant="outline-secondary" onClick={prev}>Back</Button>}
        {step < steps.length - 1 ? (
          <Button variant="primary" onClick={next}>Next</Button>
        ) : (
          <Button variant="success" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" /> Booking...
              </>
            ) : (
              'Confirm & Book'
            )}
          </Button>
        )}
      </Modal.Footer>

      <style jsx="true">{`
        .modal-content.modern-modal {
          max-width: 50%;
          margin: auto;
          border-radius: 1rem;
          padding: 1rem 1.5rem;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
        }

        .modal-title {
          font-weight: 600;
          font-size: 1.25rem;
        }

        .modal-body,
        .modal-footer {
          padding: 1.5rem;
        }

        .form-control,
        .form-select {
          border-radius: 0.5rem;
        }

        .list-group-item {
          border: none;
          padding: 0.75rem 1rem;
          background-color: #f9f9f9;
          margin-bottom: 0.5rem;
          border-radius: 0.5rem;
        }
      `}</style>
    </Modal>
  );
}

export default TesterModal;
